import React from 'react';
import useDraggable from '../hooks/useDraggable';
import useTrackPageView from '../hooks/useTrackPageView';
import { useTranslation } from '../i18n/LanguageContext';

// Veri sorumlusu / şirket bilgisi — Panoram (panoram.com.tr) ile AYNI tüzel kişilik, kullanıcı
// onayıyla birebir taşındı. Sadece işlenen veri kategorileri/amaçlar TrendSniper'a (OKX API
// anahtarları, işlem/bot verileri) göre uyarlandı.
const COMPANY_TR   = 'Uygar Teknoloji Bilişim Hizmetleri';
const COMPANY_MAIL = 'info@uygarteknoloji.com.tr';
const COMPANY_ADDR = 'Dikkaldırım Mah. Hurmalı Sk. Alparslan Apt. A Bl. No:17/8 Osmangazi/BURSA';

const Section = ({ title, children }) => (
  <div className="sh-section">
    <h3 className="sh-section-title">{title}</h3>
    {children}
  </div>
);

function LegalModal({ title, lastUpdated, onClose, pageName, children }) {
  const { t } = useTranslation();
  const { offset: dragOffset, onMouseDown: handleHeaderMouseDown } = useDraggable();
  useTrackPageView(pageName);
  return (
    <div className="sh-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="sh-modal" style={{ transform: `translate(${dragOffset.x}px, ${dragOffset.y}px)` }}>
        <div className="sh-header" onMouseDown={handleHeaderMouseDown}>
          <div>
            <h2 className="sh-title">{title}</h2>
            <p className="sh-subtitle">{lastUpdated}</p>
          </div>
          <button className="sh-close" onClick={onClose}>✕</button>
        </div>
        <div className="sh-body">
          {children}
        </div>
        <div className="sh-footer">
          <button className="btn btn-start" onClick={onClose}>{t('common.understood')}</button>
        </div>
      </div>
    </div>
  );
}

export function KvkkNoticeModal({ onClose }) {
  const { lang } = useTranslation();
  const isEn = lang === 'en';
  return (
    <LegalModal
      onClose={onClose}
      pageName="Kayit - KVKK Aydinlatma Metni"
      title={isEn ? 'Personal Data Processing Notice' : 'Kişisel Verilerin İşlenmesine İlişkin Aydınlatma Metni'}
      lastUpdated={isEn ? 'Last updated: July 2026' : 'Son güncelleme: Temmuz 2026'}
    >
      <Section title={isEn ? '1. Data Controller' : '1. Veri Sorumlusu'}>
        <p className="sh-text">
          {isEn
            ? <>{COMPANY_TR} ("Company") acts as the data controller within the scope of the TrendSniper
              platform, under Law No. 6698 on the Protection of Personal Data ("KVKK"). Contact: {COMPANY_MAIL}</>
            : <>{COMPANY_TR} ("Şirket"), TrendSniper platformu kapsamında 6698 sayılı Kişisel Verilerin
              Korunması Kanunu ("KVKK") çerçevesinde veri sorumlusu sıfatıyla hareket etmektedir.
              İletişim: {COMPANY_MAIL}</>}
        </p>
      </Section>

      <Section title={isEn ? '2. Personal Data Processed' : '2. İşlenen Kişisel Veriler'}>
        <p className="sh-text">
          {isEn
            ? <>Full name, e-mail address, password (stored only as a bcrypt hash, never in plain
              text), OKX Global / OKX TR API credentials that you enter yourself (stored encrypted,
              used solely to connect to your own exchange account), trade/bot/strategy data (opened
              positions, strategy parameters, trade history), IP address, device/browser information,
              and platform usage/log records.</>
            : <>Ad-soyad, e-posta adresi, şifre (yalnızca bcrypt hash olarak saklanır, asla düz metin
              değil), kendi girdiğiniz OKX Global / OKX TR API bilgileri (şifreli saklanır, sadece
              kendi borsa hesabınıza bağlanmak için kullanılır), işlem/bot/strateji verileri (açılan
              pozisyonlar, strateji parametreleri, işlem geçmişi), IP adresi, cihaz/tarayıcı bilgileri
              ve platform kullanım/log kayıtları.</>}
        </p>
      </Section>

      <Section title={isEn ? '3. Purposes and Legal Grounds of Processing' : '3. İşleme Amaçları ve Hukuki Dayanakları'}>
        <ul className="sh-text" style={{ margin: 0, paddingLeft: 18, lineHeight: 1.8 }}>
          <li>{isEn ? 'Membership and account management — KVKK Art. 5/2-c (performance of a contract)' : 'Üyelik ve hesap yönetimi — KVKK m.5/2-c (sözleşmenin ifası)'}</li>
          <li>{isEn ? 'Providing the algorithmic trading automation service (OKX API integration) — KVKK Art. 5/2-c' : 'Algoritmik işlem otomasyonu hizmetinin sunulması (OKX API entegrasyonu) — KVKK m.5/2-c'}</li>
          <li>{isEn ? 'Sending commercial electronic messages, only with your separate explicit consent — KVKK Art. 5/1' : 'Ticari elektronik ileti gönderimi, sadece ayrıca verdiğiniz açık rıza ile — KVKK m.5/1'}</li>
          <li>{isEn ? 'Fulfilling legal obligations — KVKK Art. 5/2-ç' : 'Yasal yükümlülüklerin yerine getirilmesi — KVKK m.5/2-ç'}</li>
          <li>{isEn ? 'Protecting legitimate interests (security, fraud prevention, logging) — KVKK Art. 5/2-f' : 'Meşru menfaatlerin korunması (güvenlik, dolandırıcılık önleme, loglama) — KVKK m.5/2-f'}</li>
        </ul>
      </Section>

      <Section title={isEn ? '4. Transfer of Data' : '4. Verilerin Aktarılması'}>
        <p className="sh-text">
          {isEn
            ? <>Your personal data may be shared with infrastructure/hosting providers, e-mail/SMS
              service providers, and public authorities where legally required. <strong>Note:</strong> the
              order requests TrendSniper sends to OKX Global / OKX TR are NOT a "transfer" of your
              personal data to a third party — they are trade instructions carried out on YOUR OWN
              exchange account, at your own direction, using the API credentials you provided. Where
              data is transferred abroad, the safeguards required under KVKK Art. 9 are ensured.</>
            : <>Kişisel verileriniz; altyapı/barındırma hizmet sağlayıcıları, e-posta/SMS hizmet
              sağlayıcıları ve yasal zorunluluk halinde kamu kurum ve kuruluşlarıyla paylaşılabilir.
              <strong> Not:</strong> TrendSniper'ın OKX Global / OKX TR'ye gönderdiği emirler, kişisel
              verilerinizin üçüncü bir tarafa "aktarımı" DEĞİLDİR — bunlar, sizin sağladığınız API
              bilgileriyle, KENDİ borsa hesabınızda, sizin talimatınızla gerçekleştirilen işlem
              talimatlarıdır. Yurt dışı aktarımlarda KVKK m.9 kapsamında gerekli güvenceler sağlanır.</>}
        </p>
      </Section>

      <Section title={isEn ? '5. Retention Period' : '5. Saklama Süresi'}>
        <p className="sh-text">
          {isEn
            ? 'Your data is retained for as long as your account remains active, and afterward for the periods mandated by applicable legislation starting from your account deletion request. Records related to commercial message consent are kept for 3 years as required by the Electronic Commerce regulations.'
            : 'Verileriniz, hesabınız aktif olduğu süre boyunca ve hesap silme talebinizden itibaren ilgili mevzuatta öngörülen yasal süreler dahilinde saklanır. Ticari ileti onayına ilişkin kayıtlar, İYS mevzuatı gereği 3 yıl tutulur.'}
        </p>
      </Section>

      <Section title={isEn ? '6. Your Rights' : '6. Haklarınız'}>
        <p className="sh-text">{isEn ? 'Under KVKK Art. 11, you have the right to:' : 'KVKK m.11 uyarınca aşağıdaki haklara sahipsiniz:'}</p>
        <ul className="sh-text" style={{ margin: 0, paddingLeft: 18, lineHeight: 1.8 }}>
          <li>{isEn ? 'Learn whether your personal data is being processed' : 'Kişisel verilerinizin işlenip işlenmediğini öğrenme'}</li>
          <li>{isEn ? 'Request information if it has been processed' : 'İşlenmişse buna ilişkin bilgi talep etme'}</li>
          <li>{isEn ? 'Learn the purpose of processing and whether it is used accordingly' : 'İşlenme amacını öğrenme ve amacına uygun kullanılıp kullanılmadığını sorgulama'}</li>
          <li>{isEn ? 'Request correction if processed incompletely or incorrectly' : 'Verilerin eksik/yanlış işlenmesi halinde düzeltilmesini isteme'}</li>
          <li>{isEn ? 'Request deletion/destruction within the scope of KVKK Art. 7' : 'KVKK m.7 çerçevesinde silinmesini/yok edilmesini talep etme'}</li>
          <li>{isEn ? 'Object to a result against you arising from analysis via automated systems' : 'İşlemenin otomatik sistemlerle gerçekleştirilmesi halinde aleyhine doğan sonuca itiraz etme'}</li>
          <li>{isEn ? 'Request compensation for damages' : 'Zararın giderilmesini talep etme'}</li>
        </ul>
        <p className="sh-text">
          {isEn ? <>You can send your requests to {COMPANY_MAIL}.</> : <>Başvurularınızı {COMPANY_MAIL} adresine iletebilirsiniz.</>}
        </p>
      </Section>
    </LegalModal>
  );
}

export function TermsModal({ onClose }) {
  const { lang } = useTranslation();
  const isEn = lang === 'en';
  return (
    <LegalModal
      onClose={onClose}
      pageName="Kayit - Kullanim Kosullari"
      title={isEn ? 'Terms of Use' : 'Kullanım Koşulları'}
      lastUpdated={isEn ? 'Last updated: July 2026' : 'Son güncelleme: Temmuz 2026'}
    >
      <Section title={isEn ? '1. Scope of Service' : '1. Hizmet Kapsamı'}>
        <p className="sh-text">
          {isEn
            ? 'TrendSniper is an algorithmic analysis and automation platform that lets you open manual or bot-driven trades on your own OKX Global / OKX TR Futures or Spot account, based on the strategy and parameters you choose. By registering on the platform, you are deemed to have accepted these terms.'
            : 'TrendSniper; kullanıcının kendi OKX Global / OKX TR Vadeli İşlemler veya Spot hesabı üzerinde, kullanıcının seçtiği strateji ve parametrelere göre manuel veya bot tabanlı işlem açılmasını sağlayan bir algoritmik analiz ve otomasyon platformudur. Platforma kayıt olarak bu koşulları kabul etmiş sayılırsınız.'}
        </p>
      </Section>

      <Section title={isEn ? '2. Not Investment Advice' : '2. Yatırım Tavsiyesi Değildir'}>
        <p className="sh-text">
          {isEn
            ? <><strong>TrendSniper never provides investment advice, financial consultancy, or a
              "buy/sell" recommendation in any form.</strong> The strategies, indicators, and analyses
              offered on the platform are purely for informational and automation purposes.
              Cryptocurrency and futures trading carry a high level of risk, no profit is guaranteed,
              and full responsibility for your trading decisions and their outcomes belongs to you.</>
            : <><strong>TrendSniper hiçbir şekilde yatırım tavsiyesi, finansal danışmanlık ya da
              "al-sat" önerisi sunmaz.</strong> Platformda sunulan strateji, gösterge ve analizler
              tamamen bilgilendirme ve otomasyon amaçlıdır. Kripto para ve vadeli işlem yatırımları
              yüksek risk içerir, kâr garantisi verilmez; alınan kararların ve sonuçlarının
              sorumluluğu tamamen size aittir.</>}
        </p>
      </Section>

      <Section title={isEn ? '3. Scope of Authorization' : '3. Yetkilendirme Kapsamı'}>
        <p className="sh-text">
          {isEn
            ? <>TrendSniper only opens or closes trades on your OKX TR / OKX Global account WITHIN the
              strategy, trading pair, and parameters that YOU have selected and approved. No trade,
              transfer, or withdrawal is ever carried out outside of your own selection or without
              your instruction. You may stop a running bot or manual tracking at any time.</>
            : <>TrendSniper, OKX TR / OKX Global hesabınızda YALNIZCA sizin seçtiğiniz ve onayladığınız
              strateji, parite ve parametreler dahilinde işlem açar/kapatır. Sizin seçiminiz ve
              talimatınız dışında hiçbir işlem, transfer veya çekim işlemi asla gerçekleştirilmez.
              Çalışan bir botu veya manuel takibi istediğiniz an durdurabilirsiniz.</>}
        </p>
      </Section>

      <Section title={isEn ? '4. Fees' : '4. Ücretlendirme'}>
        <p className="sh-text">
          {isEn
            ? 'No fee, commission, or deduction beyond the subscription fee announced within the app is ever requested from you. TrendSniper cannot directly access your exchange account balance/assets and collect a fee on its own behalf.'
            : 'Uygulama içinde ilan edilen abonelik ücreti dışında sizden hiçbir ek ücret, komisyon ya da kesinti talep edilmez. TrendSniper, borsa hesabınızdaki bakiyeye/varlıklara doğrudan erişip kendi adına ücret tahsil edemez.'}
        </p>
      </Section>

      <Section title={isEn ? '5. Account Security' : '5. Hesap Güvenliği'}>
        <p className="sh-text">
          {isEn
            ? 'You are responsible for the security of your account. Do not share your password with anyone. We strongly recommend creating your OKX API key with "trade only" permission (no withdrawal permission). If you notice unauthorized access to your account, report it immediately to info@uygarteknoloji.com.tr.'
            : 'Hesabınızın güvenliğinden siz sorumlusunuz. Şifrenizi kimseyle paylaşmayınız. OKX API anahtarınızı, çekim iznine sahip OLMAYAN, sadece "işlem" (trade-only) izniyle oluşturmanızı önemle tavsiye ederiz. Hesabınızda yetkisiz bir erişim fark ettiğinizde derhal info@uygarteknoloji.com.tr adresine bildirin.'}
        </p>
      </Section>

      <Section title={isEn ? '6. Usage Limits' : '6. Platform Kullanım Sınırları'}>
        <p className="sh-text">{isEn ? 'The following are strictly prohibited on the platform:' : 'Platformda aşağıdakiler kesinlikle yasaktır:'}</p>
        <ul className="sh-text" style={{ margin: 0, paddingLeft: 18, lineHeight: 1.8 }}>
          <li>{isEn ? 'Attempting to access another user\'s account or data' : 'Başka bir kullanıcının hesabına veya verisine erişmeye çalışmak'}</li>
          <li>{isEn ? 'Reverse-engineering, decompiling, or attacking the platform\'s infrastructure' : 'Platformun altyapısını tersine mühendislik yapmak, parçalarına ayırmak veya saldırıya uğratmak'}</li>
          <li>{isEn ? 'Any use contrary to Turkish law and international regulations' : 'Türk hukuku ve uluslararası mevzuata aykırı her türlü kullanım'}</li>
        </ul>
      </Section>

      <Section title={isEn ? '7. Service Interruptions and Changes' : '7. Hizmet Kesintileri ve Değişiklikler'}>
        <p className="sh-text">
          {isEn
            ? 'TrendSniper reserves the right to change, update, or temporarily suspend platform features without prior notice, including due to OKX API outages or maintenance work. Planned maintenance is announced in advance where possible.'
            : 'TrendSniper, OKX API kesintileri veya bakım çalışmaları dahil, önceden bildirmeksizin platform özelliklerini değiştirme, güncelleme veya geçici olarak askıya alma hakkını saklı tutar. Planlı bakım çalışmaları mümkün olduğunca önceden duyurulur.'}
        </p>
      </Section>

      <Section title={isEn ? '8. Limitation of Liability' : '8. Sorumluluk Sınırlaması'}>
        <p className="sh-text">
          {isEn
            ? 'TrendSniper cannot be held liable for losses arising from market risk, OKX outages/API errors, or internet/server interruptions outside its control. The service is provided "as is", without any profit guarantee.'
            : 'TrendSniper; piyasa riski, OKX kesintileri/API hataları ya da kontrolü dışındaki internet/sunucu kesintilerinden doğan zararlardan sorumlu tutulamaz. Hizmet, hiçbir kâr garantisi verilmeksizin "olduğu gibi" sunulmaktadır.'}
        </p>
      </Section>

      <Section title={isEn ? '9. Governing Law' : '9. Uygulanacak Hukuk'}>
        <p className="sh-text">
          {isEn
            ? 'These terms are subject to Turkish law. Bursa Courts and Enforcement Offices have jurisdiction over any disputes.'
            : 'Bu koşullar Türk Hukuku\'na tabidir. Anlaşmazlıklarda Bursa Mahkemeleri ve İcra Daireleri yetkilidir.'}
        </p>
      </Section>
    </LegalModal>
  );
}

export function PrivacyModal({ onClose }) {
  const { lang } = useTranslation();
  const isEn = lang === 'en';
  return (
    <LegalModal
      onClose={onClose}
      pageName="Kayit - Gizlilik Politikasi"
      title={isEn ? 'Privacy Policy' : 'Gizlilik Politikası'}
      lastUpdated={isEn ? 'Last updated: July 2026' : 'Son güncelleme: Temmuz 2026'}
    >
      <Section title={isEn ? '1. Data We Collect' : '1. Topladığımız Veriler'}>
        <p className="sh-text">
          {isEn ? 'Account information:' : 'Hesap bilgileri:'} {isEn ? 'full name, e-mail, password hash.' : 'ad-soyad, e-posta, şifre hash\'i.'}<br/>
          {isEn ? 'Exchange information:' : 'Borsa bilgileri:'} {isEn ? 'OKX Global / OKX TR API key/secret/passphrase you enter, stored encrypted.' : 'sizin girdiğiniz OKX Global / OKX TR API anahtarı/gizli anahtarı/parolası, şifreli olarak saklanır.'}<br/>
          {isEn ? 'Trading data:' : 'İşlem verileri:'} {isEn ? 'strategy settings, bot sessions, trade history, logs.' : 'strateji ayarları, bot oturumları, işlem geçmişi, log kayıtları.'}<br/>
          {isEn ? 'Technical data:' : 'Teknik veriler:'} {isEn ? 'IP address, browser type, operating system, access times.' : 'IP adresi, tarayıcı türü, işletim sistemi, erişim zamanları.'}<br/>
          {isEn ? 'Cookie data:' : 'Çerez verileri:'} {isEn ? 'session-management cookies.' : 'oturum yönetimine ilişkin çerezler.'}
        </p>
      </Section>

      <Section title={isEn ? '2. Cookies' : '2. Çerezler'}>
        <p className="sh-text">
          {isEn
            ? 'Our platform uses mandatory cookies for session management (staying logged in), and Google Analytics cookies to anonymously measure which screens are viewed (no personal, transaction, or form data is ever sent). We do not use advertising cookies.'
            : 'Platformumuz oturum yönetimi (giriş durumunun korunması) için zorunlu çerez ve hangi ekranların görüntülendiğini anonim olarak ölçmek için Google Analytics çerezi kullanır (hiçbir kişisel, işlem veya form verisi gönderilmez). Reklam çerezi kullanılmaz.'}
        </p>
      </Section>

      <Section title={isEn ? '3. Use of Data' : '3. Verilerin Kullanımı'}>
        <ul className="sh-text" style={{ margin: 0, paddingLeft: 18, lineHeight: 1.8 }}>
          <li>{isEn ? 'Providing and improving the platform service' : 'Platform hizmetinin sağlanması ve iyileştirilmesi'}</li>
          <li>{isEn ? 'Account verification and security' : 'Hesap doğrulama ve güvenlik'}</li>
          <li>{isEn ? 'Sending commercial messages (SMS, e-mail), only where you have given consent' : 'Onay vermeniz halinde ticari ileti gönderimi (SMS, e-posta)'}</li>
          <li>{isEn ? 'Fulfilling legal obligations' : 'Yasal yükümlülüklerin yerine getirilmesi'}</li>
          <li>{isEn ? 'Usage statistics and service-quality analysis' : 'Kullanım istatistikleri ve hizmet kalitesi analizi'}</li>
        </ul>
      </Section>

      <Section title={isEn ? '4. Third-Party Services' : '4. Üçüncü Taraf Hizmetler'}>
        <p className="sh-text">{isEn ? 'The platform makes use of the following third-party services:' : 'Platform aşağıdaki üçüncü taraf hizmetlerden yararlanmaktadır:'}</p>
        <ul className="sh-text" style={{ margin: 0, paddingLeft: 18, lineHeight: 1.8 }}>
          <li>{isEn ? 'Google Analytics (anonymous page-view measurement)' : 'Google Analytics (anonim sayfa görüntüleme ölçümü)'}</li>
          <li>{isEn ? 'Google OAuth (optional social login)' : 'Google OAuth (isteğe bağlı sosyal giriş)'}</li>
          <li>{isEn ? 'OKX Global / OKX TR — used SOLELY to connect to YOUR OWN exchange account at your own instruction; your API credentials are never used for TrendSniper\'s own purposes or shared with any other party.' : 'OKX Global / OKX TR — SADECE sizin talimatınızla KENDİ borsa hesabınıza bağlanmak için kullanılır; API bilgileriniz TrendSniper\'ın kendi amacıyla asla kullanılmaz ve başka hiçbir tarafla paylaşılmaz.'}</li>
        </ul>
        <p className="sh-text">
          {isEn ? 'These services\' own privacy policies apply to their part of the processing.' : 'Bu hizmetlerin kendi gizlilik politikaları kendi işleme faaliyetleri için geçerlidir.'}
        </p>
      </Section>

      <Section title={isEn ? '5. Data Security' : '5. Veri Güvenliği'}>
        <p className="sh-text">
          {isEn
            ? 'Your data is protected against unauthorized access through HTTPS encryption, secure password hashing (bcrypt), encrypted storage of your exchange API credentials, and access control. That said, please note that no data transmission over the internet is 100% secure.'
            : 'Verileriniz; HTTPS şifrelemesi, güvenli parola hashleme (bcrypt), borsa API bilgilerinizin şifreli saklanması ve erişim denetimi ile yetkisiz erişime karşı korunmaktadır. Bununla birlikte internet üzerinden hiçbir veri iletiminin %100 güvenli olmadığını hatırlatırız.'}
        </p>
      </Section>

      <Section title={isEn ? '6. Data Retention and Deletion' : '6. Veri Saklama ve Silme'}>
        <p className="sh-text">
          {isEn
            ? 'When you delete your account, your profile and trading data are removed from our systems within 30 days. Data falling under a legal retention obligation (e.g. commercial-message consent records) is kept for the period required by the applicable legislation.'
            : 'Hesabınızı sildiğinizde, profil ve işlem verileriniz 30 gün içinde sistemlerimizden kaldırılır. Yasal saklama yükümlülüğü kapsamındaki veriler (örn. ticari ileti onay kayıtları) ilgili mevzuatın öngördüğü süre boyunca saklanır.'}
        </p>
      </Section>

      <Section title={isEn ? '7. Contact' : '7. İletişim'}>
        <p className="sh-text">
          {isEn ? 'For privacy-related questions: ' : 'Gizlilik ile ilgili sorularınız için: '}{COMPANY_MAIL}<br/>
          {isEn ? 'Mailing address: ' : 'Posta adresi: '}{COMPANY_TR}, {COMPANY_ADDR}
        </p>
      </Section>
    </LegalModal>
  );
}
